import { TeamMember } from "@/types/database";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Loader2, MoreHorizontal, UserCog } from "lucide-react";

interface TeamMembersListProps {
  teamMembers: TeamMember[];
  isLoading: boolean;
  onRemove: (memberId: string) => void;
  onUpdateRole: (memberId: string, role: 'admin' | 'member') => void;
  onResendInvitation: (memberId: string) => void;
  isRemoving: boolean;
  isResending: boolean;
}

export const TeamMembersList = ({ 
  teamMembers, 
  isLoading, 
  onRemove, 
  onUpdateRole,
  onResendInvitation,
  isRemoving,
  isResending 
}: TeamMembersListProps) => {
  if (isLoading) {
    return (
      <div className="py-4 text-center">
        <Loader2 className="h-6 w-6 animate-spin mx-auto text-gray-400" />
        <p className="text-sm text-gray-500 mt-2">Loading team members...</p>
      </div>
    );
  }

  if (teamMembers.length === 0) {
    return (
      <div className="py-6 text-center border rounded-md bg-gray-50">
        <UserCog className="h-10 w-10 mx-auto text-gray-400 mb-2" />
        <p className="text-gray-600">No team members yet</p>
        <p className="text-sm text-gray-500 mt-1">
          Invite team members to collaborate and share pixels
        </p>
      </div>
    );
  }

  // Group members by status
  const activeMembers = teamMembers.filter(member => member.status === 'active');
  const pendingMembers = teamMembers.filter(member => member.status === 'pending');

  return (
    <div className="space-y-6">
      {activeMembers.length > 0 && (
        <div>
          <h3 className="text-sm font-medium mb-2">Active Members</h3>
          <div className="divide-y border rounded-md overflow-hidden">
            {activeMembers.map(member => (
              <div key={member.id} className="p-3 bg-white flex justify-between items-center">
                <div>
                  <div className="flex items-center">
                    <Avatar className="h-8 w-8 mr-2">
                      <AvatarFallback>{member.email.substring(0, 2).toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="text-sm font-medium">{member.email}</p>
                      <Badge variant={member.role === 'admin' ? "secondary" : "outline"} className="mt-1">
                        {member.role}
                      </Badge>
                    </div>
                  </div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuLabel>Actions</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {member.role === 'member' && (
                      <DropdownMenuItem onClick={() => onUpdateRole(member.id, 'admin')}>
                        Make Admin
                      </DropdownMenuItem>
                    )}
                    {member.role === 'admin' && (
                      <DropdownMenuItem onClick={() => onUpdateRole(member.id, 'member')}>
                        Remove Admin
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem 
                      className="text-red-600" 
                      onClick={() => onRemove(member.id)}
                    >
                      Remove
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {pendingMembers.length > 0 && (
        <div>
          <h3 className="text-sm font-medium mb-2">Pending Invitations</h3>
          <div className="divide-y border rounded-md overflow-hidden">
            {pendingMembers.map(member => (
              <div key={member.id} className="p-3 bg-white flex justify-between items-center">
                <div>
                  <div className="flex items-center">
                    <Avatar className="h-8 w-8 mr-2">
                      <AvatarFallback>{member.email.substring(0, 2).toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="text-sm font-medium">{member.email}</p>
                      <div className="flex items-center mt-1">
                        <Badge variant="outline" className="text-amber-600 border-amber-200 bg-amber-50">
                          Pending
                        </Badge>
                        <Badge variant={member.role === 'admin' ? "secondary" : "outline"} className="ml-2">
                          {member.role}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuLabel>Actions</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => onResendInvitation(member.id)}>
                      Resend Invitation
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      className="text-red-600" 
                      onClick={() => onRemove(member.id)}
                    >
                      Cancel Invitation
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
